import { EmbedFrame } from "@/components/EmbedFrame";
import { Reveal } from "@/components/Reveal";
import { HOME } from "@/constants/testIds";
import { APPLE_URL, COVER_ART, SOUNDCLOUD_URL, SPOTIFY_EMBED, SPOTIFY_URL } from "@/lib/constants";

const Fallback = () => (
    <div data-testid={HOME.spotifyFallback} className="flex flex-col items-start gap-6 p-8 sm:p-10">
        <img
            src={COVER_ART}
            width="180"
            height="180"
            alt="Deep End Grooves playlist cover art: overlapping terracotta, indigo, navy and gold circles on a cream label"
            className="w-44 h-44 object-cover"
            loading="lazy"
        />
        <p className="prose-body max-w-[38ch] text-sm">
            the player could not load. the full playlist is there on spotify.
        </p>
        <a
            href={SPOTIFY_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="eyebrow border-b border-[var(--ink)] pb-1 hover:text-[var(--terracotta-ink)] transition-colors duration-200"
        >
            open on spotify
        </a>
    </div>
);

const mirrors = [
    { label: "apple music", href: APPLE_URL, testId: HOME.appleCta },
    { label: "soundcloud", href: SOUNDCLOUD_URL, testId: HOME.soundcloudCta },
];

export const Playlists = () => (
    <section id="playlists" data-testid={HOME.playlists} aria-label="the playlist — deep end grooves on spotify" className="border-b border-[var(--rule)]">
        <div className="mx-auto max-w-[1240px] px-6 sm:px-10 lg:px-16 py-20 sm:py-24 lg:py-28">
            <Reveal>
                <p className="eyebrow text-[var(--terracotta-ink)]">02 — the playlist</p>
                <h2 className="font-display mt-6 text-4xl sm:text-5xl lg:text-6xl max-w-[22ch]">
                    the deep house playlist
                </h2>
            </Reveal>

            <Reveal delay={0.05}>
                <div
                    data-testid={HOME.spotifyPanel}
                    className="mt-12 sm:mt-14 border border-[var(--ink)] bg-[var(--cream)]"
                >
                    <div className="grid lg:grid-cols-[1fr_1.15fr]">
                        <div className="p-8 sm:p-10 lg:p-12 lg:border-r border-[var(--rule)] flex flex-col justify-between gap-10">
                            <div>
                                <p className="eyebrow text-[var(--terracotta-ink)]">spotify</p>
                                <h3 className="font-display mt-5 text-3xl sm:text-4xl">deep end grooves</h3>
                                <p className="prose-body mt-6 max-w-[42ch] text-base">
                                    deep house, deep tech and dub — sequenced to be played front to back. late sets,
                                    long drives, quiet rooms. new additions every week, nothing kept in that no longer
                                    belongs.
                                </p>
                            </div>
                            <div>
                                <a
                                    href={SPOTIFY_URL}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid={HOME.spotifyFollowCta}
                                    className="inline-block border border-[var(--ink)] bg-[var(--ink)] px-8 py-4 text-base font-bold uppercase tracking-[0.08em] text-[var(--cream)] hover:bg-[var(--terracotta-ink)] hover:border-[var(--terracotta-ink)] transition-colors duration-200"
                                >
                                    follow on spotify
                                </a>
                                <p className="eyebrow mt-6 text-[var(--ink)] opacity-80">
                                    no sign-up here · saved straight to your library
                                </p>
                            </div>
                        </div>
                        <div className="border-t lg:border-t-0 border-[var(--rule)]">
                            <EmbedFrame
                                src={SPOTIFY_EMBED}
                                title="Deep End Grooves playlist on Spotify"
                                height={520}
                                testId={HOME.spotifyEmbed}
                                fallback={<Fallback />}
                            />
                        </div>
                    </div>
                </div>
            </Reveal>

            <Reveal delay={0.1}>
                <div data-testid={HOME.mirrors} className="mt-14 sm:mt-16">
                    <ul className="border-t border-[var(--rule)]">
                        {mirrors.map((m) => (
                            <li key={m.label} className="border-b border-[var(--rule)]">
                                <a
                                    href={m.href}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid={m.testId}
                                    className="flex items-baseline justify-between gap-6 py-5 hover:text-[var(--terracotta-ink)] transition-colors duration-200"
                                >
                                    <span className="font-display text-2xl sm:text-3xl">{m.label}</span>
                                    <span className="eyebrow opacity-80">open the mirror</span>
                                </a>
                            </li>
                        ))}
                    </ul>
                    <p
                        data-testid={HOME.mirrorsNote}
                        className="eyebrow mt-5 text-[var(--ink)] opacity-70"
                    >
                        The Apple Music and SoundCloud playlists follow the Spotify playlist.
                    </p>
                </div>
            </Reveal>
        </div>
    </section>
);
